import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Loader2,
  RefreshCcw,
  Save,
  Home,
  List,
  Info,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const PLACEMENTS = [
  { key: "home_screen", label: "Home screen", icon: Home },
  { key: "place_list", label: "Places list", icon: List },
  { key: "place_details", label: "Place details", icon: Info },
];

export default function AdConfig() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ad-config");
      setCfg(data);
    } catch (e) {
      toast.error("Failed to load ad config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const { data } = await api.put("/ad-config", {
        enabled: cfg.enabled,
        frequency_minutes: Number(cfg.frequency_minutes),
        interstitial_frequency: Number(cfg.interstitial_frequency),
        max_per_session: Number(cfg.max_per_session),
        placements: cfg.placements,
        video_ad: cfg.video_ad,
      });
      setCfg(data);
      toast.success("Ad config saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!confirm("Reset ad config to defaults? Video rules will be replaced.")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/ad-config/reset");
      setCfg(data);
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error("Reset failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return (
      <div className="p-10 text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading ad configuration…
      </div>
    );
  }

  const patch = (path, value) => {
    setCfg((prev) => {
      const next = structuredClone(prev);
      let cur = next;
      for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]];
      cur[path[path.length - 1]] = value;
      return next;
    });
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <div className="flex items-start justify-between gap-4 mb-8 animate-in-up flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
            Monetisation
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
            Ad Configuration
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl">
            Control global ad behaviour, per-placement banners and interstitials for the
            NearMe app. Changes take effect on next app config fetch.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={load}
            data-testid="ads-refresh-btn"
            className="h-10"
          >
            <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button
            variant="outline"
            onClick={reset}
            data-testid="ads-reset-btn"
            className="h-10"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Reset defaults
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="ads-save-btn"
            className="h-10"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save changes
          </Button>
        </div>
      </div>

      {/* Global toggles */}
      <section
        className="bg-card border border-border rounded-2xl p-6 mb-6 animate-in-up"
        data-testid="ads-global-section"
      >
        <div className="text-sm font-bold mb-5">Global settings</div>
        <div className="flex items-center justify-between py-3 border-b border-border">
          <div>
            <Label className="text-sm font-semibold">Ads enabled</Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Master switch for all ad placements
            </p>
          </div>
          <Switch
            checked={!!cfg.enabled}
            onCheckedChange={(v) => patch(["enabled"], v)}
            data-testid="ads-enabled-switch"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-5">
          <NumField
            label="Cooldown (minutes)"
            hint="Min gap between interstitials"
            value={cfg.frequency_minutes}
            onChange={(v) => patch(["frequency_minutes"], v)}
            testId="freq-mins-input"
          />
          <NumField
            label="Interstitial frequency"
            hint="Show every N user actions"
            value={cfg.interstitial_frequency}
            onChange={(v) => patch(["interstitial_frequency"], v)}
            testId="interstitial-freq-input"
          />
          <NumField
            label="Max per session"
            hint="Cap ads per app session"
            value={cfg.max_per_session}
            onChange={(v) => patch(["max_per_session"], v)}
            testId="max-per-session-input"
          />
        </div>
      </section>

      {/* Placements */}
      <section className="bg-card border border-border rounded-2xl p-6 animate-in-up">
        <div className="text-sm font-bold mb-4">Placements</div>
        <Tabs defaultValue="home_screen">
          <TabsList className="mb-6">
            {PLACEMENTS.map((p) => (
              <TabsTrigger
                key={p.key}
                value={p.key}
                data-testid={`placement-tab-${p.key}`}
              >
                <p.icon className="w-4 h-4 mr-1.5" /> {p.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {PLACEMENTS.map((p) => {
            const placement = cfg.placements?.[p.key] || {};
            return (
              <TabsContent key={p.key} value={p.key} className="space-y-6">
                <PlacementBlock
                  title="Banner"
                  data={placement.banner || {}}
                  onChange={(k, v) => patch(["placements", p.key, "banner", k], v)}
                  hasPosition
                  hasPlaceholderTitle
                  testPrefix={`banner-${p.key}`}
                />
                <PlacementBlock
                  title="Interstitial"
                  data={placement.interstitial || {}}
                  onChange={(k, v) =>
                    patch(["placements", p.key, "interstitial", k], v)
                  }
                  testPrefix={`interstitial-${p.key}`}
                />
              </TabsContent>
            );
          })}
        </Tabs>
      </section>
    </div>
  );
}

const NumField = ({ label, hint, value, onChange, testId }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </Label>
    <Input
      type="number"
      value={value ?? 0}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testId}
      className="mt-1.5 h-11"
    />
    {hint && <p className="text-xs text-muted-foreground mt-1.5">{hint}</p>}
  </div>
);

const PlacementBlock = ({
  title,
  data,
  onChange,
  hasPosition,
  hasPlaceholderTitle,
  testPrefix,
}) => (
  <div className="border border-border rounded-xl p-5">
    <div className="flex items-center justify-between mb-4">
      <div className="text-sm font-bold">{title}</div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Enabled</span>
        <Switch
          checked={!!data.enabled}
          onCheckedChange={(v) => onChange("enabled", v)}
          data-testid={`${testPrefix}-enabled-switch`}
        />
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {hasPosition && (
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Position
          </Label>
          <select
            value={data.position || "bottom"}
            onChange={(e) => onChange("position", e.target.value)}
            data-testid={`${testPrefix}-position-select`}
            className="mt-1.5 w-full h-11 rounded-lg border border-input bg-background px-3 text-sm font-medium"
          >
            <option value="top">Top</option>
            <option value="bottom">Bottom</option>
          </select>
        </div>
      )}
      {hasPlaceholderTitle && (
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Placeholder title
          </Label>
          <Input
            value={data.placeholderTitle || ""}
            onChange={(e) => onChange("placeholderTitle", e.target.value)}
            data-testid={`${testPrefix}-placeholder-input`}
            className="mt-1.5 h-11"
          />
        </div>
      )}
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Android Unit ID
        </Label>
        <Input
          value={data.androidUnitId || ""}
          onChange={(e) => onChange("androidUnitId", e.target.value)}
          placeholder="ca-app-pub-xxx/xxx"
          data-testid={`${testPrefix}-android-unit-input`}
          className="mt-1.5 h-11 mono text-sm"
        />
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          iOS Unit ID
        </Label>
        <Input
          value={data.iosUnitId || ""}
          onChange={(e) => onChange("iosUnitId", e.target.value)}
          placeholder="ca-app-pub-xxx/xxx"
          data-testid={`${testPrefix}-ios-unit-input`}
          className="mt-1.5 h-11 mono text-sm"
        />
      </div>
    </div>
  </div>
);
